import axios from "axios";
import React, { useState, useEffect } from "react";
import "@fontsource/roboto/400.css";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Unstable_Grid2";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import Stack from "@mui/material/Stack";

import Ondara from "../assets/ondara.png";
import LoginIlustration from "./Elements/LoginIlustration";

function PasswordGenerate(props) {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Statuses: 'loading' | 'invalid_user' | 'already_has_password' | 'can_generate' | 'saved'
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");
  const [userId, setUserId] = useState(null);

  const handleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const email = urlParams.get("email");
    const id = urlParams.get("id");

    if (!email || !id) {
      setStatus("invalid_user");
      setMessage("El correo electrónico es inválido. Por favor, contacte con Ondara.");
      setLoading(false);
      return;
    }

    axios
      .post(props.config.backend_api + "checkgenerate.php", {
        email: email,
        id: id,
      })
      .then((response) => {
        const res = response.data;
        if (res.status === "success") {
          setStatus("can_generate");
          setUserId(res.data?.userId);
        } else if (res.status === "already_has_password") {
          setStatus("already_has_password");
          setMessage(res.message || "La contraseña ya ha sido generada.");
        } else {
          setStatus("invalid_user");
          setMessage(
            res.message ||
              "El correo electrónico es inválido. Por favor, contacte con Ondara."
          );
        }
      })
      .catch((error) => {
        setStatus("invalid_user");
        const errMsg =
          error.response?.data?.message ||
          "El correo electrónico es inválido. Por favor, contacte con Ondara.";
        setMessage(errMsg);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [props.config.backend_api]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!password) {
      setMessage("Por favor, introduce una contraseña.");
      return;
    }

    setSubmitting(true);
    axios
      .post(props.config.backend_api + "reset.php", {
        password: password,
        userId: userId,
      })
      .then((response) => {
        if (response.data.status === "success") {
          setStatus("saved");
          setMessage("Se ha generado la contraseña correctamente.");
        } else {
          setMessage(
            response.data.message || "Ocurrió un error al guardar la contraseña."
          );
        }
      })
      .catch((error) => {
        setMessage(
          error.response?.data?.message ||
            "Ocurrió un error al guardar la contraseña."
        );
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <Container disableGutters maxWidth={false}>
      <Grid container spacing={0}>
        <Grid xs={12} lg={6}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "100vh",
              p: 2,
            }}
          >
            <Stack
              sx={{ mt: 2, mb: 2 }}
              spacing={2}
              alignItems="center"
              width={340}
            >
              <img src={Ondara} alt="Ondara Capital" width="180" />

              <Typography variant="h2" align="center">
                Generar contraseña
              </Typography>

              {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                  <CircularProgress />
                </Box>
              )}

              {!loading && status === "invalid_user" && (
                <>
                  <Alert severity="error" sx={{ width: "100%" }}>
                    {message}
                  </Alert>
                  <Typography variant="body2" align="center" color="text.secondary">
                    Si crees que se trata de un error, por favor contacta con{" "}
                    <Link href="mailto:contacto@ondara.group">
                      contacto@ondara.group
                    </Link>
                    .
                  </Typography>
                  <Box sx={{ width: "100%", textAlign: "center", mt: 2 }}>
                    <Link underline="none" href="/login">
                      Ir a la pantalla de inicio
                    </Link>
                  </Box>
                </>
              )}

              {!loading && status === "already_has_password" && (
                <>
                  <Alert severity="info" sx={{ width: "100%" }}>
                    {message}
                  </Alert>
                  <Typography variant="body1" align="center">
                    La contraseña para esta cuenta ya se encuentra configurada. Si no la recuerdas, puedes recuperarla.
                  </Typography>
                  <Button
                    variant="contained"
                    href="/recover"
                    sx={{ width: "100%", mt: 2 }}
                  >
                    Recuperar contraseña
                  </Button>
                  <Box sx={{ width: "100%", textAlign: "center", mt: 1 }}>
                    <Link underline="none" href="/login">
                      Ir a la pantalla de inicio
                    </Link>
                  </Box>
                </>
              )}

              {!loading && status === "can_generate" && (
                <form onSubmit={handleSubmit} style={{ width: "100%" }}>
                  <Stack spacing={2} alignItems="center">
                    <Typography variant="body1" align="center">
                      Introduce tu nueva contraseña para acceder a la plataforma.
                    </Typography>

                    {message && (
                      <Alert severity="error" sx={{ width: "100%" }}>
                        {message}
                      </Alert>
                    )}

                    <TextField
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={handlePasswordChange}
                      variant="filled"
                      label="Nueva contraseña"
                      fullWidth
                      required
                      InputProps={{
                        endAdornment: (
                          <IconButton onClick={handleShowPassword} edge="end">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        ),
                      }}
                    />

                    <Button
                      variant="contained"
                      type="submit"
                      disabled={submitting}
                      sx={{ alignSelf: "stretch" }}
                    >
                      {submitting ? "Guardando..." : "Guardar contraseña"}
                    </Button>

                    <Box sx={{ width: "100%", textAlign: "right", mt: 1 }}>
                      <Link underline="none" href="/login">
                        Ir a la pantalla de inicio
                      </Link>
                    </Box>
                  </Stack>
                </form>
              )}

              {!loading && status === "saved" && (
                <>
                  <Alert severity="success" sx={{ width: "100%" }}>
                    {message}
                  </Alert>
                  <Typography variant="body1" align="center">
                    Tu contraseña ha sido guardada con éxito. Ya puedes iniciar sesión.
                  </Typography>
                  <Button
                    variant="contained"
                    href="/login"
                    sx={{ width: "100%", mt: 2 }}
                  >
                    Iniciar sesión
                  </Button>
                </>
              )}
            </Stack>
          </Box>
        </Grid>
        <Grid xs={12} lg={6}>
          <LoginIlustration />
        </Grid>
      </Grid>
    </Container>
  );
}

export default PasswordGenerate;
